/**
 * New mouse enter handler using Bresenham interpolation.
 * Fills gaps between touch points for reliable mobile drawing.
 */

import { ColourPoint, GridBoxPath, Point, SpecialTile } from "../types";
import { PathPoint, getDirection, Direction, pointsEqual, pointKey } from "../types/path";
import { getIntermediateCells, isInBounds } from "../utils/lineTracing";
import { stopDrawing } from "../utils/stopDrawing";
import { playSFX } from "../utils/playSFX";

type HandleMouseEnterNewParams = {
  x: number;
  y: number;
  drawing: boolean;
  prevBox: { x: number; y: number } | null;
  circles: ColourPoint[];
  currentColor: string | null;
  wallTiles: Point[];
  specialTiles: SpecialTile[];
  path: { [key: string]: GridBoxPath };
  setDrawing: React.Dispatch<React.SetStateAction<boolean>>;
  setPath: React.Dispatch<React.SetStateAction<{ [key: string]: GridBoxPath }>>;
  setCompletedPaths: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
  setCurrentColor: React.Dispatch<React.SetStateAction<string | null>>;
  stageEffects: string[];
  setPuzzle: React.Dispatch<React.SetStateAction<{
    circles: ColourPoint[];
    size: number;
    wallTiles: Point[];
    specialTiles: SpecialTile[];
    stageEffects: string[];
    colorCount?: number;
    backgroundColor?: string;
  }>>;
  setPrevBox: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  gridSize: number;
};

/**
 * Check if a cell is blocked (wall, other path, or blocking special tile).
 */
function isCellBlocked(
  point: PathPoint,
  currentColor: string,
  path: { [key: string]: GridBoxPath },
  wallTiles: Point[],
  circles: ColourPoint[],
  specialTiles: SpecialTile[],
  completedColors: Set<string>
): boolean {
  const key = pointKey(point);

  // Wall check
  if (wallTiles.some((w) => w.x === point.x && w.y === point.y)) {
    return true;
  }

  // Other path check (different color) - but allow magic boxes (multiple colors can pass through)
  const isMagicBox = specialTiles.some(
    (s) => s.x === point.x && s.y === point.y && s.tileType.includes("magic-box")
  );
  if (path[key] && path[key].color !== currentColor && !isMagicBox) {
    return true;
  }

  // Different color circle check
  const circleAtPoint = circles.find((c) => c.x === point.x && c.y === point.y);
  if (circleAtPoint && circleAtPoint.color !== currentColor) {
    return true;
  }

  // Lock tile check
  const lockTile = specialTiles.find(
    (s) => s.x === point.x && s.y === point.y && s.tileType === "lock"
  );
  if (lockTile && !completedColors.has("green")) {
    return true;
  }

  // Color-specific tile check
  const colorSpecificTile = specialTiles.find(
    (s) =>
      s.x === point.x &&
      s.y === point.y &&
      s.tileType.includes("colour-specific")
  );
  if (colorSpecificTile && colorSpecificTile.color !== currentColor) {
    return true;
  }

  return false;
}

/**
 * Check if a cell can be entered from a given direction.
 */
function canEnterFromDirection(
  point: PathPoint,
  entryDirection: Direction,
  specialTiles: SpecialTile[]
): boolean {
  // Direction-specific tiles (includes painter boxes which enforce direction)
  const horizontalOnly = specialTiles.find(
    (s) =>
      s.x === point.x &&
      s.y === point.y &&
      (s.tileType.includes("horizontal-only") || s.tileType === "painter-box-horizontal")
  );
  if (horizontalOnly && (entryDirection === "up" || entryDirection === "down")) {
    return false;
  }

  const verticalOnly = specialTiles.find(
    (s) =>
      s.x === point.x &&
      s.y === point.y &&
      (s.tileType.includes("vertical-only") || s.tileType === "painter-box-vertical")
  );
  if (verticalOnly && (entryDirection === "left" || entryDirection === "right")) {
    return false;
  }

  // Arrow tiles - cannot enter from the direction the arrow points (the exit side)
  // e.g., arrow-up can be entered from left, right, or bottom - but NOT from top
  const arrowTile = specialTiles.find(
    (s) =>
      s.x === point.x &&
      s.y === point.y &&
      s.tileType.includes("arrow-")
  );
  if (arrowTile) {
    const arrowDir = arrowTile.tileType.split("-").pop() as Direction;
    // Block entry from arrow's exit side (entering from top for arrow-up means moving down)
    const blockedEntryDirection = getOppositeDirection(arrowDir);
    if (entryDirection === blockedEntryDirection) {
      return false;
    }
  }

  return true;
}

/**
 * Check if a cell allows exiting in a given direction.
 * Directional tiles restrict both entry AND exit.
 */
function canExitInDirection(
  point: PathPoint,
  exitDirection: Direction,
  specialTiles: SpecialTile[]
): boolean {
  // Direction-specific tiles restrict exit direction too
  const horizontalOnly = specialTiles.find(
    (s) =>
      s.x === point.x &&
      s.y === point.y &&
      (s.tileType.includes("horizontal-only") || s.tileType === "painter-box-horizontal")
  );
  if (horizontalOnly && (exitDirection === "up" || exitDirection === "down")) {
    return false;
  }

  const verticalOnly = specialTiles.find(
    (s) =>
      s.x === point.x &&
      s.y === point.y &&
      (s.tileType.includes("vertical-only") || s.tileType === "painter-box-vertical")
  );
  if (verticalOnly && (exitDirection === "left" || exitDirection === "right")) {
    return false;
  }

  // Arrow tiles - can only exit in the direction the arrow points
  const arrowTile = specialTiles.find(
    (s) =>
      s.x === point.x &&
      s.y === point.y &&
      s.tileType.includes("arrow-")
  );
  if (arrowTile) {
    const arrowDir = arrowTile.tileType.split("-").pop() as Direction;
    if (exitDirection !== arrowDir) {
      return false;
    }
  }

  return true;
}

function getOppositeDirection(dir: Direction): Direction {
  switch (dir) {
    case "up": return "down";
    case "down": return "up";
    case "left": return "right";
    case "right": return "left";
  }
}

/**
 * Check if a point is already in the current path (for backtracking).
 */
function isInCurrentPath(
  point: PathPoint,
  currentColor: string,
  path: { [key: string]: GridBoxPath }
): boolean {
  const key = pointKey(point);
  return path[key]?.color === currentColor;
}

/**
 * Get the path segment for backtracking by following connections from a starting point.
 */
function getPathSegmentFromPoint(
  startPoint: PathPoint,
  color: string,
  path: { [key: string]: GridBoxPath },
  circles: ColourPoint[]
): PathPoint[] {
  // Find the circle that starts this path
  const startCircle = circles.find(
    (c) => c.color === color && path[pointKey({ x: c.x, y: c.y })]?.color === color
  );

  if (!startCircle) {
    return [];
  }

  const visited = new Set<string>();
  const segment: PathPoint[] = [];
  let current: PathPoint | null = { x: startCircle.x, y: startCircle.y };

  while (current && !visited.has(pointKey(current))) {
    visited.add(pointKey(current));
    segment.push(current);

    const currentKey = pointKey(current);
    const cell = path[currentKey];
    if (!cell || cell.color !== color) break;

    // Find next connected cell
    let next: PathPoint | null = null;
    const neighbors: [Direction, PathPoint][] = [
      ["up", { x: current.x, y: current.y - 1 }],
      ["down", { x: current.x, y: current.y + 1 }],
      ["left", { x: current.x - 1, y: current.y }],
      ["right", { x: current.x + 1, y: current.y }],
    ];

    for (const [dir, neighbor] of neighbors) {
      if (cell[dir] && !visited.has(pointKey(neighbor))) {
        next = neighbor;
        break;
      }
    }

    current = next;
  }

  return segment;
}

/**
 * Handle mouse/touch entering a cell with Bresenham interpolation.
 */
export function handleMouseEnterNew({
  x,
  y,
  drawing,
  prevBox,
  circles,
  currentColor,
  wallTiles,
  specialTiles,
  path,
  setDrawing,
  setPath,
  setCompletedPaths,
  setCurrentColor,
  stageEffects,
  setPuzzle,
  setPrevBox,
  gridSize,
}: HandleMouseEnterNewParams): void {
  if (!drawing || !currentColor || !prevBox) {
    return;
  }

  const targetPoint: PathPoint = { x, y };
  const prevPoint: PathPoint = { x: prevBox.x, y: prevBox.y };

  // Same cell - no change
  if (pointsEqual(targetPoint, prevPoint)) {
    return;
  }

  // Out of bounds
  if (!isInBounds(targetPoint, gridSize)) {
    return;
  }

  // Get completed colors for special tile checks
  const completedColors = new Set<string>();
  // Note: We'd need access to completedPaths here, but it's not passed in
  // For now, we'll handle this in the individual tile checks

  // Get all intermediate cells using Bresenham
  const cells = getIntermediateCells(prevPoint, targetPoint);

  if (cells.length === 0) {
    return;
  }

  let lastValidPoint = prevPoint;
  let shouldStopDrawing = false;
  let activeColor = currentColor; // Tracks color changes from painter boxes
  const cellsToAdd: { point: PathPoint; entryDir: Direction; isWarpExit?: boolean; color?: string; exitDir?: Direction; isMagicBoxEntry?: boolean }[] = [];

  // Process each intermediate cell
  for (const cell of cells) {
    const entryDirection = getDirection(lastValidPoint, cell);
    if (!entryDirection) {
      break;
    }

    // Check for backtracking (entering a cell already in our path)
    // For painter boxes, also check if we're backtracking from the transformed color side
    const cellKey = pointKey(cell);
    const painterBoxAtCell = specialTiles.find(
      (s) => s.x === cell.x && s.y === cell.y && s.tileType.includes("painter-box")
    );

    // Determine if this is a backtrack situation
    let isBacktrack = isInCurrentPath(cell, currentColor, path);
    let backtrackColor = currentColor;

    // For painter boxes: check if we're backtracking from the other color side
    // e.g., we're drawing white and the painter box cell has red (the entry color)
    if (!isBacktrack && painterBoxAtCell && path[cellKey]) {
      const boxColor = path[cellKey].color;
      // Check if this painter box connects our current color to the box's color
      // i.e., the box has one of our colors and we're approaching from the other side
      if (boxColor === painterBoxAtCell.color && currentColor === "white") {
        isBacktrack = true;
        backtrackColor = boxColor; // Use the box's color for backtracking
      } else if (boxColor === "white" && currentColor === painterBoxAtCell.color) {
        isBacktrack = true;
        backtrackColor = boxColor;
      }
    }

    if (isBacktrack) {
      const lastValidKey = pointKey(lastValidPoint);
      const targetCell = path[cellKey];
      const sourceCell = path[lastValidKey];

      // Index-based backtracking validation:
      // Only allow backtracking if target cell's index < source cell's index (going backwards)
      // If going "forward" (to higher index), it's not backtracking - block it
      if (targetCell?.index !== undefined && sourceCell?.index !== undefined) {
        if (targetCell.index >= sourceCell.index) {
          // Target has same or higher index - trying to go forward into own path, block
          break;
        }
      }

      // Special handling for magic boxes and painter boxes - only allow backtracking from the exit side
      const magicBoxForBacktrack = specialTiles.find(
        (s) => s.x === cell.x && s.y === cell.y && s.tileType.includes("magic-box")
      );

      if (magicBoxForBacktrack || painterBoxAtCell) {
        // Calculate which direction the path exits the box toward us
        const boxCell = path[cellKey];
        // entryDirection is the direction we're MOVING (e.g., "left" if going from right to left)
        // The box exit pointing toward us is the OPPOSITE (e.g., "right" points back at us)
        const exitDirTowardUs = getOppositeDirection(entryDirection);

        // Check if box actually has a connection in this direction (meaning path exits this way)
        if (!boxCell || !boxCell[exitDirTowardUs]) {
          // Box doesn't exit toward us - we're entering from wrong side
          // Stop drawing entirely to prevent cursor from being positioned to undraw the real path
          stopDrawing({ setDrawing });
          return;
        }

        // Mobile touch fix: On mobile, touch events fire repeatedly even when finger doesn't move.
        // After passing through a painter/magic box, prevBox is set to the exit cell.
        // If the next touch event fires before React updates state, or if finger is still
        // hovering over the box, it looks like backtracking when it's not.
        // Fix: If we're at the exit cell (sourceCell) and trying to go back to the box (targetCell),
        // and the indices are consecutive (exit is target+1), just skip - don't undo.
        if (targetCell?.index !== undefined && sourceCell?.index !== undefined) {
          if (sourceCell.index === targetCell.index + 1) {
            // We're one step past the box, trying to go back - this is likely a touch hover issue
            // Just skip this move entirely rather than undoing
            return;
          }
        }
      }

      // Backtracking detected - erase all cells with index > target's index
      // For painter boxes, pass the box's color so it cleans up correctly
      const continueFrom = handleBacktrackStep(lastValidPoint, cell, backtrackColor, path, setPath, specialTiles, circles, painterBoxAtCell);

      if (continueFrom) {
        // Continue drawing from the returned point
        setPrevBox(continueFrom);
        // For painter boxes, we may need to update the current color back to the entry color
        if (painterBoxAtCell && continueFrom) {
          const entryColor = painterBoxAtCell.color;
          if (currentColor === "white") {
            setCurrentColor(entryColor || "tomato");
          } else {
            setCurrentColor("white");
          }
        }
      } else {
        // No valid point to continue from (e.g., entry cell was deleted), stop drawing
        stopDrawing({ setDrawing });
      }
      return;
    }

    // Check if cell is blocked
    if (isCellBlocked(cell, currentColor, path, wallTiles, circles, specialTiles, completedColors)) {
      break;
    }

    // Check direction constraints - both exit from current cell AND entry to target cell
    if (!canExitInDirection(lastValidPoint, entryDirection, specialTiles)) {
      break;
    }
    if (!canEnterFromDirection(cell, entryDirection, specialTiles)) {
      break;
    }

    // Check for magic box
    const magicBox = specialTiles.find(
      (s) => s.x === cell.x && s.y === cell.y && s.tileType.includes("magic-box")
    );
    if (magicBox) {
      const result = handleMagicBox(magicBox, entryDirection, cell, lastValidPoint);
      if (!result.canEnter || !result.exitDir) {
        break;
      }

      // Check if the exit cell is valid before allowing entry into magic box
      const exitCell = getNeighborPoint(cell, result.exitDir);

      // Exit cell must be in bounds
      if (!isInBounds(exitCell, gridSize)) {
        break;
      }

      // Exit cell must not be blocked
      if (isCellBlocked(exitCell, currentColor, path, wallTiles, circles, specialTiles, completedColors)) {
        break;
      }

      // Exit cell must allow entry from the magic box's exit direction
      if (!canEnterFromDirection(exitCell, result.exitDir, specialTiles)) {
        break;
      }

      // All validations passed - play the sound
      playSFX("SFX/magic-box.wav");

      // Magic box: add with both entry and exit edges (separate, not connected)
      // Multiple colors can pass through the same magic box
      cellsToAdd.push({ point: cell, entryDir: entryDirection, color: activeColor, isMagicBoxEntry: true, exitDir: result.exitDir });

      // Add the exit cell to continue the path (entry direction is the magic box's exit direction)
      cellsToAdd.push({ point: exitCell, entryDir: result.exitDir, color: activeColor });

      // Check if exit cell completes the path (lands on end circle)
      const magicBoxEndCircle = circles.find(
        (c) => c.x === exitCell.x && c.y === exitCell.y && c.color === activeColor
      );
      const magicBoxStartCircle = circles.find(
        (c) =>
          path[pointKey({ x: c.x, y: c.y })]?.color === activeColor &&
          c.color === activeColor
      );
      if (magicBoxEndCircle && magicBoxStartCircle &&
          !pointsEqual({ x: magicBoxEndCircle.x, y: magicBoxEndCircle.y }, { x: magicBoxStartCircle.x, y: magicBoxStartCircle.y })) {
        // Path complete via magic box
        addCellsToPath(cellsToAdd, currentColor, path, setPath, prevPoint);
        setCompletedPaths((prev) => ({ ...prev, [activeColor]: true }));
        handlePathCompletion(activeColor, stageEffects, specialTiles, path, setPuzzle);
        stopDrawing({ setDrawing });
        setPrevBox(exitCell);
        return;
      }

      // Jump to the exit cell and continue drawing from there
      lastValidPoint = exitCell;
      continue;
    }

    // Check for warp
    const warpTile = specialTiles.find(
      (s) => s.x === cell.x && s.y === cell.y && s.tileType === "warp"
    );
    if (warpTile) {
      const result = handleWarp(warpTile, cell, activeColor, path, specialTiles);
      if (result.teleportTo) {
        // Add entry warp with direction we came from
        cellsToAdd.push({ point: cell, entryDir: entryDirection, color: activeColor });
        // Add warp exit with no direction (isWarpExit flag)
        cellsToAdd.push({ point: result.teleportTo, entryDir: entryDirection, isWarpExit: true, color: activeColor });
        lastValidPoint = result.teleportTo;
        continue;
      }
    }

    // Check for painter box
    const painterBox = specialTiles.find(
      (s) =>
        s.x === cell.x &&
        s.y === cell.y &&
        (s.tileType === "painter-box-horizontal" || s.tileType === "painter-box-vertical")
    );
    if (painterBox) {
      // Only allow entry if color is painter box color or white
      if (activeColor !== painterBox.color && activeColor !== "white") {
        break;
      }

      // Calculate new color FIRST - we need this to check if exit is blocked
      const newColor = activeColor === "white" ? (painterBox.color || "tomato") : "white";

      // Painter box is straight line - exit direction is same as entry direction
      const exitDir = entryDirection;

      // Check if the exit cell is valid before allowing entry
      const exitCell = getNeighborPoint(cell, exitDir);

      // Exit cell must be in bounds
      if (!isInBounds(exitCell, gridSize)) {
        break;
      }

      // Exit cell must not be blocked - check with NEW color since that's what will be drawn there
      if (isCellBlocked(exitCell, newColor, path, wallTiles, circles, specialTiles, completedColors)) {
        break;
      }

      // Exit cell must allow entry from the painter box's exit direction
      if (!canEnterFromDirection(exitCell, exitDir, specialTiles)) {
        break;
      }

      // All validations passed - play the sound
      playSFX("SFX/painter-box.wav");

      // Add painter box cell with entry color, entry and exit edges
      cellsToAdd.push({ point: cell, entryDir: entryDirection, color: activeColor, isMagicBoxEntry: true, exitDir });

      // Add the exit cell with new color
      cellsToAdd.push({ point: exitCell, entryDir: exitDir, color: newColor });

      // Check if exit cell completes the path (circle matching newColor)
      const painterBoxEndCircle = circles.find(
        (c) => c.x === exitCell.x && c.y === exitCell.y && c.color === newColor
      );
      if (painterBoxEndCircle) {
        // Path complete via painter box
        addCellsToPath(cellsToAdd, currentColor, path, setPath, prevPoint);
        setCompletedPaths((prev) => ({ ...prev, [newColor]: true }));
        handlePathCompletion(newColor, stageEffects, specialTiles, path, setPuzzle);
        stopDrawing({ setDrawing });
        setPrevBox(exitCell);
        return;
      }

      // Jump to the exit cell and continue drawing with new color
      lastValidPoint = exitCell;
      activeColor = newColor;

      // Update the current color state so subsequent drawing uses the new color
      setCurrentColor(newColor);
      continue;
    }

    // Cell is valid - add it
    cellsToAdd.push({ point: cell, entryDir: entryDirection, color: activeColor });
    lastValidPoint = cell;

    // Check for path completion
    const endCircle = circles.find(
      (c) => c.x === cell.x && c.y === cell.y && c.color === currentColor
    );
    const startCircle = circles.find(
      (c) =>
        path[pointKey({ x: c.x, y: c.y })]?.color === currentColor &&
        c.color === currentColor
    );
    if (endCircle && startCircle && !pointsEqual({ x: endCircle.x, y: endCircle.y }, { x: startCircle.x, y: startCircle.y })) {
      // Path complete
      shouldStopDrawing = true;

      // Add the cells first
      if (cellsToAdd.length > 0) {
        addCellsToPath(cellsToAdd, currentColor, path, setPath, prevPoint);
      }

      setCompletedPaths((prev) => ({ ...prev, [currentColor]: true }));
      handlePathCompletion(currentColor, stageEffects, specialTiles, path, setPuzzle);
      stopDrawing({ setDrawing });
      setPrevBox(cell);
      return;
    }
  }

  // Add all valid cells to path
  if (cellsToAdd.length > 0) {
    addCellsToPath(cellsToAdd, currentColor, path, setPath, prevPoint);
    setPrevBox(lastValidPoint);
  }

  if (shouldStopDrawing) {
    stopDrawing({ setDrawing });
  }
}

/**
 * Add cells to the path with proper directional connections.
 */
function addCellsToPath(
  cells: { point: PathPoint; entryDir: Direction; isWarpExit?: boolean; color?: string; exitDir?: Direction; isMagicBoxEntry?: boolean }[],
  defaultColor: string,
  path: { [key: string]: GridBoxPath },
  setPath: React.Dispatch<React.SetStateAction<{ [key: string]: GridBoxPath }>>,
  startPoint: PathPoint
): void {
  setPath((prev) => {
    const newPath = { ...prev };
    let prevPoint = startPoint;

    // Get the starting index from the start point cell
    const startKey = pointKey(startPoint);
    let currentIndex = (newPath[startKey]?.index ?? -1) + 1;

    for (const { point, entryDir, isWarpExit, color: cellColor, exitDir: customExitDir, isMagicBoxEntry } of cells) {
      const key = pointKey(point);
      const prevKey = pointKey(prevPoint);
      const entryConnection = getOppositeDirection(entryDir); // Direction pointing BACK to prev
      const color = cellColor || defaultColor;

      // For warp exits, don't update the previous cell or set directions
      // Just create the cell with color and no directions (central dot)
      if (isWarpExit) {
        const existingCell = newPath[key];
        newPath[key] = {
          up: existingCell?.up || false,
          down: existingCell?.down || false,
          left: existingCell?.left || false,
          right: existingCell?.right || false,
          color,
          index: currentIndex++,
        };
        prevPoint = point;
        continue;
      }

      // Update previous cell to point to this cell
      if (newPath[prevKey]) {
        newPath[prevKey] = {
          ...newPath[prevKey],
          [entryDir]: true, // Direction from prev to current
        };
      }

      // Create or update current cell
      const existingCell = newPath[key];

      if (isMagicBoxEntry && customExitDir) {
        // Magic box: show both entry and exit edges (separate, not visually connected)
        // Multiple colors can pass through - preserve existing edges from other colors
        newPath[key] = {
          up: existingCell?.up || false,
          down: existingCell?.down || false,
          left: existingCell?.left || false,
          right: existingCell?.right || false,
          color,
          index: currentIndex++,
          [entryConnection]: true,  // Entry edge (where path comes from)
          [customExitDir]: true,    // Exit edge (where path goes to)
        };
      } else if (isMagicBoxEntry) {
        // Magic box entry without exit (shouldn't happen, but handle gracefully)
        newPath[key] = {
          up: existingCell?.up || false,
          down: existingCell?.down || false,
          left: existingCell?.left || false,
          right: existingCell?.right || false,
          color,
          index: currentIndex++,
          [entryConnection]: true,
        };
      } else {
        // Normal cell: just entry connection back to prev
        newPath[key] = {
          up: existingCell?.up || false,
          down: existingCell?.down || false,
          left: existingCell?.left || false,
          right: existingCell?.right || false,
          color,
          index: currentIndex++,
          [entryConnection]: true, // Direction back to prev
        };
      }

      prevPoint = point;
    }

    return newPath;
  });
}

/**
 * Handle backtracking: erase all cells with index > target cell's index.
 * This allows fast swipes to erase multiple cells at once.
 *
 * For magic/painter boxes: disconnects both entry and exit sides, returns the entry cell to continue from.
 * Returns the point to continue drawing from (for boxes, this is the entry cell).
 */
function handleBacktrackStep(
  _prevPoint: PathPoint,
  backtrackPoint: PathPoint,
  color: string,
  path: { [key: string]: GridBoxPath },
  setPath: React.Dispatch<React.SetStateAction<{ [key: string]: GridBoxPath }>>,
  specialTiles: SpecialTile[],
  circles: ColourPoint[],
  painterBoxTile?: SpecialTile
): PathPoint | null {
  const backtrackKey = pointKey(backtrackPoint);
  const targetCell = path[backtrackKey];

  if (!targetCell || targetCell.index === undefined) {
    return null;
  }

  const targetIndex = targetCell.index;

  // Check if backtracking into a magic box or painter box
  const magicBox = specialTiles.find(
    (s) =>
      s.x === backtrackPoint.x &&
      s.y === backtrackPoint.y &&
      s.tileType.includes("magic-box")
  );

  const painterBox = specialTiles.find(
    (s) =>
      s.x === backtrackPoint.x &&
      s.y === backtrackPoint.y &&
      s.tileType.includes("painter-box")
  );

  // Treat magic boxes and painter boxes the same for backtracking
  const isSpecialBox = magicBox || painterBox;

  // For special boxes (magic/painter), we need to find the entry cell to continue drawing from
  let boxEntryCell: PathPoint | null = null;

  // For painter boxes, determine the other color that needs to be cleaned up
  const otherColor = painterBoxTile
    ? (color === "white" ? painterBoxTile.color : "white")
    : null;

  setPath((prev) => {
    const newPath = { ...prev };

    // Find and delete all cells of this color with index > target index
    for (const key of Object.keys(newPath)) {
      const cell = newPath[key];
      if (cell.color === color && cell.index !== undefined && cell.index > targetIndex) {
        // Check if this cell is on a circle (don't delete circle cells, just clear connections)
        const [x, y] = key.split(",").map(Number);
        const isCircle = circles.some(
          (c) => c.x === x && c.y === y && c.color === color
        );

        if (isCircle) {
          // Clear all connections but keep the cell
          newPath[key] = {
            up: false,
            down: false,
            left: false,
            right: false,
            color,
            index: cell.index,
          };
        } else {
          delete newPath[key];
        }
      }
    }

    // For painter boxes: also clean up cells of the OTHER color (after transformation)
    // These are the cells on the exit side of the painter box
    if (painterBoxTile && otherColor) {
      for (const key of Object.keys(newPath)) {
        const cell = newPath[key];
        if (cell.color === otherColor && cell.index !== undefined && cell.index > targetIndex) {
          const [x, y] = key.split(",").map(Number);
          const isCircle = circles.some(
            (c) => c.x === x && c.y === y && c.color === otherColor
          );

          if (isCircle) {
            newPath[key] = {
              up: false,
              down: false,
              left: false,
              right: false,
              color: otherColor,
              index: cell.index,
            };
          } else {
            delete newPath[key];
          }
        }
      }
    }

    // Update the target cell: remove outgoing connections (to higher indices)
    // Keep only the connection pointing backward (to lower index)
    const updatedTargetCell = newPath[backtrackKey];
    if (updatedTargetCell) {
      const directions: Direction[] = ["up", "down", "left", "right"];
      for (const dir of directions) {
        if (updatedTargetCell[dir]) {
          // Check if this connection points to a cell that was deleted (higher index)
          const neighbor = getNeighborPoint(backtrackPoint, dir);
          const neighborKey = pointKey(neighbor);
          const neighborCell = newPath[neighborKey];

          // If neighbor doesn't exist or has higher index, remove this connection
          if (!neighborCell || (neighborCell.index !== undefined && neighborCell.index > targetIndex)) {
            newPath[backtrackKey] = { ...newPath[backtrackKey], [dir]: false };
          }
        }
      }
    }

    // Magic box / Painter box: disconnect both entry and exit sides completely
    if (isSpecialBox && newPath[backtrackKey]?.color === color) {
      const boxCell = newPath[backtrackKey];

      // Find and remove all connections from the box
      // Also find the entry cell (lower index) to continue drawing from
      const directions: Direction[] = ["up", "down", "left", "right"];
      for (const dir of directions) {
        if (boxCell[dir]) {
          // Remove this connection from box
          newPath[backtrackKey] = { ...newPath[backtrackKey], [dir]: false };

          // Find the connected cell
          const connectedCell = getNeighborPoint(backtrackPoint, dir);
          const connectedKey = pointKey(connectedCell);
          const connectedCellData = newPath[connectedKey];

          // For painter boxes, check both entry color and exit color (otherColor)
          const isEntryColorCell = connectedCellData && connectedCellData.color === color;
          const isExitColorCell = connectedCellData && otherColor && connectedCellData.color === otherColor;

          if (isEntryColorCell || isExitColorCell) {
            const cellColor = connectedCellData.color;

            // Check if this is the entry cell (lower index) - save it for continuing drawing
            // Entry cell should be the same color as box entry (color), not otherColor
            if (isEntryColorCell && connectedCellData.index !== undefined && connectedCellData.index < targetIndex) {
              boxEntryCell = connectedCell;
            }

            // Remove the connection from connected cell to magic box
            const oppositeDir = getOppositeDirection(dir);
            newPath[connectedKey] = { ...newPath[connectedKey], [oppositeDir]: false };

            // If connected cell has no connections left, delete it (unless it's a circle)
            const connectedIsCircle = circles.some(
              (c) => c.x === connectedCell.x && c.y === connectedCell.y && c.color === cellColor
            );
            const updatedConnectedCell = newPath[connectedKey];
            if (
              updatedConnectedCell &&
              !updatedConnectedCell.up &&
              !updatedConnectedCell.down &&
              !updatedConnectedCell.left &&
              !updatedConnectedCell.right &&
              !connectedIsCircle
            ) {
              delete newPath[connectedKey];
              // Only clear boxEntryCell if it was the entry cell that got deleted
              if (isEntryColorCell && connectedCellData.index !== undefined && connectedCellData.index < targetIndex) {
                boxEntryCell = null;
              }
            }
          }
        }
      }

      // Delete the box cell's path data if it has no connections left
      const finalBoxCell = newPath[backtrackKey];
      if (
        finalBoxCell &&
        finalBoxCell.color === color &&
        !finalBoxCell.up &&
        !finalBoxCell.down &&
        !finalBoxCell.left &&
        !finalBoxCell.right
      ) {
        delete newPath[backtrackKey];
      }
    }

    return newPath;
  });

  // For magic/painter boxes, return the entry cell to continue drawing from
  // For normal cells, return the backtrack point
  if (isSpecialBox) {
    return boxEntryCell;
  }
  return backtrackPoint;
}

function getNeighborPoint(point: PathPoint, dir: Direction): PathPoint {
  switch (dir) {
    case "up": return { x: point.x, y: point.y - 1 };
    case "down": return { x: point.x, y: point.y + 1 };
    case "left": return { x: point.x - 1, y: point.y };
    case "right": return { x: point.x + 1, y: point.y };
  }
}

/**
 * Handle magic box redirection.
 * Returns the exit direction for the path (where it should connect TO after the magic box).
 * Entry direction is already the direction we moved INTO the cell.
 *
 * The magic box cell needs two connections:
 * - entryConnection: points back to where we came from (opposite of entryDirection)
 * - exitConnection: points to where we're going (the redirect direction)
 */
function handleMagicBox(
  magicBox: SpecialTile,
  entryDirection: Direction,
  cell: PathPoint,
  prevPoint: PathPoint
): { canEnter: boolean; exitDir?: Direction } {
  const boxType = magicBox.tileType;

  // Magic box mappings: entry direction (direction we're moving) -> exit direction (where path continues)
  // Based on old handleMouseEnter.ts key modifications (lines 200-243)
  const mappings: { [key: string]: { [entry: string]: Direction } } = {
    "magic-box-up-left": {
      up: "right",     // diffY === -1 → x+1 (right)
      left: "down",    // diffX === -1 → y+1 (down)
      down: "left",    // diffY === 1 → x-1 (left)
      right: "up",     // diffX === 1 → y-1 (up)
    },
    "magic-box-up-right": {
      down: "right",   // diffY === 1 → x+1 (right)
      right: "down",   // diffX === 1 → y+1 (down)
      up: "left",      // diffY === -1 → x-1 (left)
      left: "up",      // diffX === -1 → y-1 (up)
    },
    "magic-box-up-down": {
      // Continues straight through - same direction
      up: "up",
      down: "down",
      left: "left",
      right: "right",
    },
  };

  const mapping = mappings[boxType];
  if (!mapping) {
    return { canEnter: true }; // Unknown type, allow normal passage
  }

  const exitDir = mapping[entryDirection];
  if (!exitDir) {
    return { canEnter: false }; // Invalid entry direction
  }

  // Note: Sound is played in the main flow after all validations pass
  return { canEnter: true, exitDir };
}

/**
 * Handle warp teleportation.
 */
function handleWarp(
  warpTile: SpecialTile,
  cell: PathPoint,
  currentColor: string,
  path: { [key: string]: GridBoxPath },
  specialTiles: SpecialTile[]
): { teleportTo?: PathPoint } {
  // Find the other warp tile with the same color
  const otherWarp = specialTiles.find(
    (s) =>
      s.tileType === "warp" &&
      s.color === warpTile.color &&
      (s.x !== cell.x || s.y !== cell.y)
  );

  if (!otherWarp) {
    return {};
  }

  const otherPoint = { x: otherWarp.x, y: otherWarp.y };
  const otherKey = pointKey(otherPoint);

  // Can't warp to a cell already in the path
  if (path[otherKey]?.color === currentColor) {
    return {};
  }

  playSFX("SFX/warp1.wav");
  return { teleportTo: otherPoint };
}

// Note: handlePainterBox removed - color logic now inline in main flow
// Sound is played after all validations pass

/**
 * Handle path completion effects.
 */
function handlePathCompletion(
  color: string,
  stageEffects: string[],
  specialTiles: SpecialTile[],
  path: { [key: string]: GridBoxPath },
  setPuzzle: React.Dispatch<React.SetStateAction<{
    circles: ColourPoint[];
    size: number;
    wallTiles: Point[];
    specialTiles: SpecialTile[];
    stageEffects: string[];
    colorCount?: number;
    backgroundColor?: string;
  }>>
): void {
  // Handle dark/light toggle for yellow path
  if (stageEffects.includes("dark") && color === "yellow") {
    playSFX("SFX/light2.wav");
    setPuzzle((prev) => ({
      ...prev,
      stageEffects: prev.stageEffects.map((e) => (e === "dark" ? "light" : e)),
    }));
  }

  // Handle lock unlocking for green path
  if (specialTiles.some((s) => s.tileType === "lock") && color === "green") {
    playSFX("SFX/lock1.wav");
    setPuzzle((prev) => ({
      ...prev,
      specialTiles: prev.specialTiles.map((s) =>
        s.tileType === "lock" ? { ...s, tileType: "unlocking" } : s
      ),
    }));

    setTimeout(() => {
      setPuzzle((prev) => ({
        ...prev,
        specialTiles: prev.specialTiles.filter((s) => s.tileType !== "unlocking"),
      }));
    }, 1000);
  }

  // Handle rotating tiles for orange path
  // Only rotate tiles that don't have a path on them
  if (
    specialTiles.some((s) => s.tileType.includes("rotating")) &&
    color === "orange"
  ) {
    // Check if any rotating tiles can actually rotate (no path on them)
    const rotatableTiles = specialTiles.filter((s) => {
      if (!s.tileType.includes("rotating")) return false;
      const key = `${s.x},${s.y}`;
      return !path[key]; // Only include tiles without a path
    });

    if (rotatableTiles.length > 0) {
      playSFX("SFX/tile-rotate.wav");
      setPuzzle((prev) => ({
        ...prev,
        specialTiles: prev.specialTiles.map((s) => {
          if (s.tileType.includes("rotating")) {
            // Check if this tile has a path on it - if so, don't rotate
            const key = `${s.x},${s.y}`;
            if (path[key]) {
              return s; // Don't rotate tiles with paths
            }
            if (s.tileType.includes("horizontal")) {
              return { ...s, tileType: s.tileType.replace("horizontal", "vertical") };
            } else if (s.tileType.includes("vertical")) {
              return { ...s, tileType: s.tileType.replace("vertical", "horizontal") };
            }
          }
          return s;
        }),
      }));
    }
  }
}
